/**
 * Module Schema & Normalisation Engine for AI Studio & Dynamic Modules.
 *
 * Enforces valid, safe, sanitized structured JSON data for all dynamically
 * generated and custom-authored modules.
 */

export const DEFAULT_PAGE_CALLOUT_TYPES = ['tip', 'warning', 'info', 'key']

/**
 * Creates an empty default module schema.
 */
export function createDefaultModule(overrides = {}) {
  const now = new Date().toISOString()
  return {
    id: overrides.id || `custom-${Date.now().toString(36)}`,
    title: 'Untitled Training Module',
    subtitle: 'Interactive training lesson',
    description: 'Learn step-by-step with practical examples.',
    track: 'sheets',
    phase: 'Core Skills',
    phaseColor: 'indigo',
    estimatedMinutes: 10,
    sourceDocUrl: '',
    status: 'draft',
    createdAt: now,
    updatedAt: now,
    author: {
      uid: '',
      displayName: 'Anonymous',
      email: '',
    },
    pages: [
      {
        title: 'Introduction & Core Concept',
        subtitle: 'Why this matters at Finder',
        content:
          'Welcome to this interactive module. In this lesson, you will learn the core concepts and practical workflows with real-world examples.\n\nWork through each step to master the material before taking the recap check.',
        callout: {
          type: 'tip',
          text: 'Take notes or test the formulas directly in your own spreadsheet as you follow along.',
        },
        questions: [
          {
            question: 'What is the primary benefit of this workflow?',
            options: [
              'Automates manual reconciliation and reduces human error',
              'Makes spreadsheets run slower',
              'Requires manual copying and pasting each week',
            ],
            answer: 0,
            explanation: 'Standardized formulas and workflows automate manual work and eliminate calculation mistakes.',
          },
        ],
      },
    ],
    quiz: {
      passingScore: 80,
      questions: [
        {
          question: 'What is the primary benefit of this workflow?',
          options: [
            'Automates manual reconciliation and reduces human error',
            'Makes spreadsheets run slower',
            'Requires manual copying and pasting each week',
          ],
          answer: 0,
          explanation: 'Standardized formulas and workflows automate manual work and eliminate calculation mistakes.',
        },
      ],
    },
    ...overrides,
  }
}

/**
 * Validates and sanitizes a module schema JSON object.
 * Returns { valid: boolean, errors: string[], normalized: object }
 */
/**
 * Normalizes question answer indices from various formats (number, string letter "A"/"B",
 * string number "0"/"1", option text match, or alternative field names).
 */
export function normalizeAnswerIndex(item, options = []) {
  if (!item || !Array.isArray(options) || options.length === 0) return 0

  const raw =
    item.answer !== undefined
      ? item.answer
      : item.correctIndex !== undefined
      ? item.correctIndex
      : item.correct_index !== undefined
      ? item.correct_index
      : item.correctAnswer !== undefined
      ? item.correctAnswer
      : item.correct_answer !== undefined
      ? item.correct_answer
      : item.correctOption !== undefined
      ? item.correctOption
      : undefined

  // 1. Direct integer in bounds
  if (typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw < options.length) {
    return raw
  }

  // 2. String parsing
  if (typeof raw === 'string') {
    const trimmed = raw.trim()

    // 2a. Digits e.g. "0", "1", "2"
    if (/^\d+$/.test(trimmed)) {
      const parsed = parseInt(trimmed, 10)
      if (parsed >= 0 && parsed < options.length) {
        return parsed
      }
      if (parsed === options.length && options.length > 0) {
        return parsed - 1 // 1-indexed fallback
      }
    }

    // 2b. Single letter e.g. "A", "B", "C", "D"
    if (/^[A-Za-z]$/.test(trimmed)) {
      const idx = trimmed.toUpperCase().charCodeAt(0) - 65
      if (idx >= 0 && idx < options.length) {
        return idx
      }
    }

    // 2c. Exact text match with one of the options
    const exactMatch = options.findIndex(
      (opt) => String(opt).trim().toLowerCase() === trimmed.toLowerCase()
    )
    if (exactMatch >= 0) {
      return exactMatch
    }

    // 2d. Substring match if sufficiently descriptive (>= 5 chars)
    if (trimmed.length >= 5) {
      const subMatch = options.findIndex((opt) => {
        const o = String(opt).trim().toLowerCase()
        return o.includes(trimmed.toLowerCase()) || trimmed.toLowerCase().includes(o)
      })
      if (subMatch >= 0) {
        return subMatch
      }
    }

    // 2e. Prefix patterns like "Option A", "A.", "Option 1", "1)"
    const letterMatch = trimmed.match(/^(?:option\s+)?([A-Za-z])(?:\b|[.:)\s]|$)/i)
    if (letterMatch) {
      const idx = letterMatch[1].toUpperCase().charCodeAt(0) - 65
      if (idx >= 0 && idx < options.length) return idx
    }

    const numMatch = trimmed.match(/^(?:option\s+)?(\d+)(?:\b|[.:)\s]|$)/i)
    if (numMatch) {
      const num = parseInt(numMatch[1], 10)
      if (num >= 0 && num < options.length) return num
      if (num > 0 && num - 1 < options.length) return num - 1
    }
  }

  // 3. Fallback: Check if explanation explicitly notes "Option B is correct" etc.
  const explanation = typeof item.explanation === 'string' ? item.explanation : ''
  if (explanation) {
    const expLetter = explanation.match(/(?:option|choice|answer)\s+([A-D])\b/i)
    if (expLetter) {
      const idx = expLetter[1].toUpperCase().charCodeAt(0) - 65
      if (idx >= 0 && idx < options.length) return idx
    }
  }

  return 0
}

/**
 * Validates and sanitizes a module schema JSON object.
 * Returns { valid: boolean, errors: string[], normalized: object }
 */
export function validateModuleSchema(input) {
  const errors = []
  if (!input || typeof input !== 'object') {
    return { valid: false, errors: ['Module data must be an object'], normalized: null }
  }

  const normalized = { ...input }

  // Ensure mandatory string fields
  if (!normalized.title || typeof normalized.title !== 'string') {
    errors.push('Module title is required.')
    normalized.title = 'Untitled Module'
  }
  normalized.title = normalized.title.trim()

  if (!normalized.id) {
    normalized.id = `custom-${Date.now().toString(36)}`
  }
  normalized.id = String(normalized.id).trim().toLowerCase().replace(/[^a-z0-9_-]/g, '-')

  if (!normalized.track) normalized.track = 'sheets'
  if (!normalized.phase) normalized.phase = 'Core Skills'
  if (!normalized.description) normalized.description = ''

  // Validate pages
  if (!Array.isArray(normalized.pages) || normalized.pages.length === 0) {
    errors.push('Module must contain at least one page.')
    normalized.pages = [
      {
        title: 'Introduction',
        content: 'Welcome to this module.',
      },
    ]
  } else {
    normalized.pages = normalized.pages.map((page, idx) => {
      const p = { ...page }
      p.title = typeof p.title === 'string' && p.title.trim() ? p.title.trim() : `Page ${idx + 1}`
      p.content = typeof p.content === 'string' ? p.content : ''
      if (p.callout && typeof p.callout === 'object') {
        p.callout = {
          type: DEFAULT_PAGE_CALLOUT_TYPES.includes(p.callout.type) ? p.callout.type : 'info',
          text: typeof p.callout.text === 'string' ? p.callout.text : '',
        }
      }
      // `table` is the ONE renderable field this validator never touched, and
      // that is exactly why an unhydrated Firestore payload could walk all the
      // way into DynamicModule's `rows.map` and take down the whole module.
      // Questions two blocks down are filtered on their shape; a table was
      // simply trusted. It is not any more.
      //
      // Anything that is not a real grid is DROPPED rather than passed on: a
      // lesson missing its table is a far better outcome than a blank page,
      // and it is the same trade the callout and question filters already make.
      if (p.table !== undefined) {
        const headers = Array.isArray(p.table?.headers)
          ? p.table.headers.map((h) => (h == null ? '' : String(h)))
          : null
        // Non-array rows are dropped individually — one malformed row should
        // not cost the reader the other nine.
        const rows = Array.isArray(p.table?.rows)
          ? p.table.rows
              .filter(Array.isArray)
              .map((row) => row.map((cell) => (cell == null ? '' : String(cell))))
          : null
        if (headers?.length && rows?.length) {
          p.table = { ...p.table, headers, rows }
        } else {
          errors.push(
            `Page ${idx + 1}: table dropped — headers and rows must both be non-empty arrays.`
          )
          delete p.table
        }
      }

      if (Array.isArray(p.questions)) {
        p.questions = p.questions
          .filter((item) => item && typeof item.question === 'string' && Array.isArray(item.options) && item.options.length >= 2)
          .map((item) => {
            const opts = item.options.map((opt) => String(opt).trim())
            const ans = normalizeAnswerIndex(item, opts)
            return {
              question: item.question.trim(),
              options: opts,
              answer: ans,
              correctIndex: ans,
              explanation: typeof item.explanation === 'string' ? item.explanation.trim() : '',
            }
          })
      }
      return p
    })
  }

  // Validate Quiz
  if (normalized.quiz && typeof normalized.quiz === 'object') {
    const q = { ...normalized.quiz }
    q.passingScore = typeof q.passingScore === 'number' && q.passingScore > 0 ? q.passingScore : 80
    if (Array.isArray(q.questions)) {
      q.questions = q.questions
        .filter((item) => item && typeof item.question === 'string' && Array.isArray(item.options) && item.options.length >= 2)
        .map((item) => {
          const opts = item.options.map((opt) => String(opt).trim())
          const ans = normalizeAnswerIndex(item, opts)
          return {
            question: item.question.trim(),
            options: opts,
            answer: ans,
            correctIndex: ans,
            explanation: typeof item.explanation === 'string' ? item.explanation.trim() : '',
          }
        })
    } else {
      q.questions = []
    }
    normalized.quiz = q
  }

  return {
    valid: errors.length === 0,
    errors,
    normalized,
  }
}

/**
 * Recursively sanitizes objects for Firestore by wrapping nested arrays (e.g. table.rows = [['a','b']])
 * into safe serialized structures since Firestore forbids arrays directly inside arrays.
 */
export function sanitizeForFirestore(val) {
  if (val === null || val === undefined) return val
  if (Array.isArray(val)) {
    // If it's a 2D array (contains any nested arrays)
    if (val.some((item) => Array.isArray(item))) {
      return { _isNestedArray: true, json: JSON.stringify(val) }
    }
    return val.map(sanitizeForFirestore)
  }
  if (typeof val === 'object') {
    const out = {}
    for (const [k, v] of Object.entries(val)) {
      out[k] = sanitizeForFirestore(v)
    }
    return out
  }
  return val
}

/**
 * Reconstitutes sanitized Firestore objects back to original JavaScript structures (e.g. 2D arrays).
 */
export function hydrateFromFirestore(val) {
  if (val === null || val === undefined) return val
  if (typeof val === 'object' && val._isNestedArray && typeof val.json === 'string') {
    try {
      return JSON.parse(val.json)
    } catch {
      return []
    }
  }
  if (Array.isArray(val)) {
    return val.map(hydrateFromFirestore)
  }
  if (typeof val === 'object') {
    const out = {}
    for (const [k, v] of Object.entries(val)) {
      out[k] = hydrateFromFirestore(v)
    }
    return out
  }
  return val
}

/**
 * Parses user prompt to see if a Google Doc or Confluence link is attached.
 */
export function extractDocLinks(promptText = '') {
  const googleDocRegex = /https:\/\/docs\.google\.com\/(?:document|spreadsheets)\/d\/([a-zA-Z0-9-_]+)/i
  const confluenceRegex = /https:\/\/[a-zA-Z0-9-_.]+\.atlassian\.net\/wiki\/[^\s]+/i
  const genericUrlRegex = /(https?:\/\/[^\s]+)/i

  const gDocMatch = promptText.match(googleDocRegex)
  const confMatch = promptText.match(confluenceRegex)
  const genericMatch = promptText.match(genericUrlRegex)

  return {
    hasGoogleDoc: !!gDocMatch,
    googleDocUrl: gDocMatch ? gDocMatch[0] : null,
    hasConfluence: !!confMatch,
    confluenceUrl: confMatch ? confMatch[0] : null,
    genericUrl: genericMatch ? genericMatch[0] : null,
  }
}



